import { firestore } from '../../../firebase';
import { type AppOffer, getRefId } from 'hooks/useAppOffers';
import { z } from 'zod';
import { isAdmin } from 'components/AdminOnly';
import { OfferStatus, PriceType } from '../Context/offerReducer';
import type { Block } from 'Views/Deals/contexts/document-context';
import { getLegacyOfferContent } from './legacyOfferContent';

type ConversionActor = { uid: string; displayName?: string; email?: string };

const documentId = z
  .string()
  .min(1)
  .max(500)
  .refine((value) => !value.includes('/') && value !== '.' && value !== '..');
const amount = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() ? Number(value) : value),
  z.number().finite().nonnegative()
);
const timestamp = z.preprocess(
  (value) =>
    value && typeof (value as { toDate?: unknown }).toDate === 'function'
      ? (value as { toDate: () => Date }).toDate()
      : value,
  z.date()
);
const sourceIdentity = z
  .object({
    createdBy: z
      .object({ uid: documentId, displayName: z.string().optional(), email: z.string().optional() })
      .passthrough(),
    createTime: timestamp,
    refId: z.string().nullish(),
  })
  .passthrough();
const sourceSchema = sourceIdentity
  .extend({
    updateTime: timestamp,
    status: z.nativeEnum(OfferStatus),
    isDeleted: z.boolean(),
    title: z.string(),
    contact: z.object({ att: z.string() }).passthrough(),
    orgId: z.string().nullish(),
    acceptTime: timestamp.nullish(),
    declineTime: timestamp.nullish(),
    invoiceContact: z.string().nullish(),
    invoiceEmail: z.string().nullish(),
    deliveryAddress: z.string().nullish(),
    offer: z
      .object({
        m2: amount,
        sensors: amount.pipe(z.number().int()),
        gps: amount.pipe(z.number().int()),
        tablets: amount.pipe(z.number().int()),
        dustMeasurements: amount.pipe(z.number().int()),
        sweepingGranules: amount,
        priceType: z.nativeEnum(PriceType),
        discount: amount.pipe(z.number().max(100)).nullish(),
        discountGranules: amount.pipe(z.number().max(100)).nullish(),
        finalPrice: amount.nullish(),
        finalLiter: amount.nullish(),
        deliveryOption: amount.pipe(z.number().int().min(1).max(6)).nullish(),
        note: z.string().nullish(),
        internalNote: z.string().nullish(),
      })
      .passthrough(),
  })
  .passthrough();
type SourceOffer = z.infer<typeof sourceSchema>;

export async function convertAppOfferToDeal({
  appOffer,
  actor,
}: {
  appOffer: Pick<AppOffer, 'id'>;
  actor: ConversionActor;
}): Promise<{ dealId: string; created: boolean }> {
  if (!documentId.safeParse(appOffer.id).success || !documentId.safeParse(actor.uid).success) {
    throw new Error('Tilbuddet eller brugeren mangler et gyldigt ID.');
  }
  if (!isAdmin(actor.uid)) {
    throw new Error(
      'Konvertering kræver en administrator, så tidligere konverteringer kan kontrolleres på tværs af ejere.'
    );
  }
  const sourceRef = firestore.collection('appOffers').doc(appOffer.id);
  const source = await sourceRef.get();
  if (!source.exists) throw new Error('Det oprindelige tilbud findes ikke længere.');
  const identity = sourceIdentity.safeParse(source.data());
  if (!identity.success)
    throw new Error(
      'Tilbuddet mangler gyldig ejer eller oprettelsesdato. Konvertering er stoppet.'
    );
  const refIds = Array.from(
    new Set([
      getRefId(identity.data.createdBy.displayName || '', identity.data.createTime.getTime()),
      ...(identity.data.refId ? [identity.data.refId] : []),
    ])
  );
  const deals = firestore.collection('deals');
  // Firestore rules enforce admin access to these unscoped lookups.
  const [converted, sameRef] = await Promise.all([
    deals.where('legacy.sourceOfferId', '==', appOffer.id).limit(2).get(),
    deals.where('initialInfo.humanReadableId', 'in', refIds).limit(2).get(),
  ]);
  if (converted.size > 1)
    throw new Error('Flere aftaler er allerede knyttet til tilbuddet. Afklar dubletterne først.');

  return firestore.runTransaction(async (transaction) => {
    const latest = await transaction.get(sourceRef);
    if (!latest.exists) throw new Error('Det oprindelige tilbud findes ikke længere.');
    const raw = latest.data()!;
    // ponytail: browser transactions cannot query. The source-document lock serializes
    // upgraded converters only; retire old clients before rollout. Use a server query
    // transaction if conversion must coexist with other migration writers.
    if (
      raw.createdBy?.uid !== identity.data.createdBy.uid ||
      raw.createdBy?.displayName !== identity.data.createdBy.displayName ||
      raw.refId !== source.data()?.refId ||
      !timestamp.safeParse(raw.createTime).success ||
      timestamp.parse(raw.createTime).getTime() !== identity.data.createTime.getTime()
    ) {
      throw new Error('Tilbuddets ejer eller reference er ændret. Genindlæs og prøv igen.');
    }
    const linkedId = raw.migratedToDealId;
    if (linkedId != null && !documentId.safeParse(linkedId).success) {
      throw new Error('Tilbuddet har en ugyldig konverteringsreference. Afklar den først.');
    }
    const targetId = linkedId || converted.docs[0]?.id || appOffer.id;
    const targetRef = deals.doc(targetId);
    const target = await transaction.get(targetRef);
    const targetData = target.data();
    const isSameSource =
      targetData?.legacy?.sourceOfferId === appOffer.id &&
      (!targetData.legacy.sourceCollection || targetData.legacy.sourceCollection === 'appOffers');
    if ((linkedId || converted.size) && (!target.exists || !isSameSource)) {
      throw new Error(
        'Den eksisterende konvertering mangler eller peger på en anden aftale. Ingen ny aftale er oprettet.'
      );
    }
    if (converted.size && converted.docs[0].id !== targetId) {
      throw new Error('Tilbuddets konverteringsreferencer er modstridende. Afklar dem først.');
    }
    if (target.exists && !isSameSource) {
      throw new Error('En anden aftale bruger allerede dette ID. Konvertering er stoppet.');
    }
    if (sameRef.docs.some((doc) => doc.id !== targetId)) {
      throw new Error(
        'Der findes allerede en anden aftale med samme tilbudsreference. Kontrollér den før konvertering.'
      );
    }
    const now = new Date();
    if (target.exists) {
      // Never rewrite a converted deal: it may have been edited since migration.
      if (!linkedId)
        transaction.update(sourceRef, {
          migratedToDealId: targetId,
          migratedToDealAt: raw.migratedToDealAt ?? targetData?.legacy?.convertedAt ?? now,
          migratedToDealBy: raw.migratedToDealBy ??
            targetData?.legacy?.convertedBy ?? {
              uid: actor.uid,
              displayName: actor.displayName || '',
              email: actor.email || '',
            },
        });
      return { dealId: targetId, created: false };
    }
    if (raw.migratedToDealAt != null || raw.migratedToDealBy != null) {
      throw new Error(
        'Tilbuddet har tidligere konverteringsmetadata, men ingen sikker destinationsreference. Afklar den først.'
      );
    }
    const parsed = sourceSchema.safeParse(raw);
    if (!parsed.success) {
      throw new Error(
        `Tilbuddet indeholder manglende eller ugyldige data (${parsed.error.issues
          .map((issue) => issue.path.join('.'))
          .join(', ')}). Ingen konvertering er udført.`
      );
    }
    const payload = buildDealPayload(parsed.data, raw, appOffer.id, actor, now);
    transaction.set(targetRef, payload);
    transaction.update(sourceRef, {
      migratedToDealId: targetId,
      migratedToDealAt: now,
      migratedToDealBy: {
        uid: actor.uid,
        displayName: actor.displayName || '',
        email: actor.email || '',
      },
    });
    return { dealId: targetId, created: true };
  });
}

function buildDealPayload(
  source: SourceOffer,
  raw: Record<string, unknown>,
  sourceId: string,
  actor: ConversionActor,
  now: Date
) {
  if (source.isDeleted) throw new Error('Et slettet tilbud kan ikke konverteres.');
  if (
    source.offer.priceType === PriceType.CUSTOM &&
    (source.offer.finalPrice == null ||
      source.offer.finalLiter == null ||
      source.offer.finalLiter <= 0)
  ) {
    throw new Error(
      'Tilpasset pris kræver en gyldig månedspris og et positivt litertal. Ingen konvertering er udført.'
    );
  }
  const refId =
    source.refId || getRefId(source.createdBy.displayName || '', source.createTime.getTime());
  const parts = getLegacyOfferContent({
    title: source.title,
    contact: source.contact,
    offer: {
      ...source.offer,
      discount: source.offer.discount ?? null,
      discountGranules: source.offer.discountGranules ?? null,
      deliveryOption: source.offer.deliveryOption ?? undefined,
      note: source.offer.note ?? null,
      internalNote: source.offer.internalNote ?? null,
    },
  });
  // ponytail: legacy pricing stays literal text; calculated products need an explicit period mapping.
  const blocks: Block[] = [];
  for (const part of parts) {
    if (part.type === 'heading') {
      blocks.push({
        id: `legacy-content-${sourceId}-${blocks.length}`,
        type: 'text',
        content: { headline: part.text, body: '', bodyHtml: '', literal: true },
      });
    } else {
      const content = blocks[blocks.length - 1].content;
      const lines = part.type === 'paragraph' ? [part.text] : part.items;
      content.body += `${content.body ? '\n\n' : ''}${lines.join('\n')}`;
      content.bodyHtml +=
        part.type === 'paragraph'
          ? `<p>${escapeHtml(part.text).replace(/\r?\n/g, '<br>')}</p>`
          : `<ul>${part.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
    }
  }
  const metadataFields = [
    'offer',
    'airCleaner',
    'contact',
    'title',
    'finalPrice',
    'orgId',
    'invoiceContact',
    'invoiceEmail',
    'deliveryAddress',
    'acceptTime',
    'declineTime',
    'pipedrive',
  ];
  return {
    ...Object.fromEntries(
      metadataFields.filter((key) => raw[key] !== undefined).map((key) => [key, raw[key]])
    ),
    createTime: raw.createTime,
    updateTime: raw.updateTime,
    createdBy: raw.createdBy,
    status: source.status,
    isDeleted: false,
    initialInfo: {
      humanReadableId: refId,
      clientName: source.title,
      contactPerson: source.contact.att,
      date: source.updateTime,
      squareMeters: String(source.offer.m2),
      deliveryFrequency: mapDeliveryOptionToInitialFrequency(source.offer.deliveryOption ?? 1),
      // Delivery intervals are not evidence of an invoicing interval.
      invoicingFrequency: 'other',
      notes: source.offer.internalNote || '',
    },
    blocks,
    legacy: {
      conversionVersion: 3,
      contentFormat: 'original-offer-text',
      sourceCollection: 'appOffers',
      sourceOfferId: sourceId,
      sourceRefId: refId,
      sourceStatus: source.status,
      sourceCreateTime: raw.createTime,
      sourceUpdateTime: raw.updateTime,
      sourceAcceptTime: raw.acceptTime ?? null,
      sourceDeclineTime: raw.declineTime ?? null,
      sourceHistoryPath: `appOffers/${sourceId}/history`,
      convertedAt: now,
      convertedBy: {
        uid: actor.uid,
        displayName: actor.displayName || '',
        email: actor.email || '',
      },
      requiresReview: true,
      sourceSnapshot: raw,
    },
  };
}

function mapDeliveryOptionToInitialFrequency(deliveryOption?: number): string {
  switch (deliveryOption) {
    case 2:
      return 'hver 2. måned';
    case 3:
      return 'hver 3. måned';
    case 4:
      return 'hver 4. måned';
    case 5:
      return 'hver 5. måned';
    case 6:
      return 'hver 6. måned';
    default:
      return 'monthly';
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
